import { useEffect } from 'react';
import { useEffectOnce } from 'react-use';
// eslint-disable-next-line no-restricted-imports -- 기존 사용처. overlay-kit 마이그레이션은 별건으로 진행한다.
import { useModal } from '@ebay/nice-modal-react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Spacing } from '@zep/ui';
import { usePlanDetail } from '../../../../services';
import { SubscribePlan } from '../../../../types';
import { pushGtmPurchaseEvent, takeGtmPurchaseAmount } from '../../../../utils/gtmMarketingEvent';
import { PhoneNumberInputDialog, PlanApplyFeature } from '../../subscription';
import { OnetimePeriod, PlanTemplateType } from '../types';
import { ConfettiExplosionWrapper, OnetimeSpaceInfo, SuccessActionButtons } from './shared';
export const PlanPaymentOnetimeSuccess = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    query
  } = useRouter();
  const spacePlanHashId = (query.spacePlanHashId || '').toString();
  const {
    data: planDetailData
  } = usePlanDetail(spacePlanHashId);
  const spaceHashId = planDetailData?.space?.hashId || '';
  const entryMapHashId = planDetailData?.space?.entryMapHashId || '';
  const maxCcu = String(planDetailData?.spacePlan?.maxCcu) || '';
  const plan = (planDetailData?.spacePlan?.planType || '').toLowerCase() as SubscribePlan;
  const thumbnailUrl = planDetailData?.space?.thumbnailUrl || '';
  const spaceName = planDetailData?.space?.name || '';
  const onetimePeriod = planDetailData?.spacePlan?.onetimePeriod || '';
  const orderId = planDetailData?.paymentInfo?.orderId || '';
  const phoneNumberInputModal = useModal(PhoneNumberInputDialog);
  const isJaPlan = planDetailData?.space?.countryCode === 'JP';
  useEffectOnce(() => {
    phoneNumberInputModal.show({
      spaceHashId,
      planTemplateType: PlanTemplateType.PRODUCT_ONETIME,
      spaceName,
      spaceUrl: `zep.us/play/${entryMapHashId}`,
      planType: plan.toUpperCase()
    });
  });

  // 마케팅 전환 추적용 purchase 신호 (GTM Custom Event 트리거로 소비).
  // orderId는 서버 조회(usePlanDetail) 값이고, 금액은 결제 직전 보관한
  // 실제 청구액(서버 VAT snapshot)을 우선 사용, 유실 시 salePrice fallback.
  // 일시납은 항상 신규 결제라 신규/변경 구분이 필요 없다.
  // 1회성 effect가 아니라 orderId 준비를 기다렸다가 push한다 (비동기 로딩으로
  // 첫 렌더에 orderId가 비어 있으면 누락되기 때문). 중복 실행은
  // pushGtmPurchaseEvent의 transaction_id dedupe가 방지한다.
  const purchaseValue = planDetailData?.spacePlan?.salePrice ?? 0;
  const purchaseCurrency = planDetailData?.spacePlan?.currency || 'KRW';
  useEffect(() => {
    if (!orderId) return;
    const chargedAmount = takeGtmPurchaseAmount(orderId);
    pushGtmPurchaseEvent({
      value: chargedAmount?.value ?? purchaseValue,
      currency: chargedAmount?.currency ?? purchaseCurrency,
      transactionId: orderId
    });
  }, [orderId, purchaseValue, purchaseCurrency]);
  return <ConfettiExplosionWrapper particleSize={8} data-sentry-element="ConfettiExplosionWrapper" data-sentry-component="PlanPaymentOnetimeSuccess" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx">
      <div className="flex h-[calc(100vh-64px)] items-center px-8">
        <div className="user-select-none mx-auto flex min-h-[470px] w-full max-w-[716px] flex-col rounded-[10px] bg-white shadow-[0_2px_10px_0_rgba(42,39,65,0.10)]">
          <div className="flex-1 px-[40px] py-[30px]">
            <h1 className="text-h5 font-[700] text-gray-900">
              {t('subscription:payment.complete.defaultTitle', '플랜 결제 완료')}
            </h1>
            <div className="flex h-[calc(100%-30px)] flex-col pt-[24px] md:flex-row">
              <section className="flex flex-col gap-3 pr-[20px]">
                <OnetimeSpaceInfo maxCcu={maxCcu} plan={plan} thumbnailUrl={thumbnailUrl} spaceName={spaceName} onetimePeriod={onetimePeriod as OnetimePeriod} data-sentry-element="OnetimeSpaceInfo" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
              </section>
              <section className="w-full min-w-[300px] pl-[20px]">
                {orderId && <div className="text-body-2-m font-[500] text-gray-500">
                    {t('subscription:payment.complete.orderNumber')} {orderId}
                  </div>}
                <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
                <div className="flex flex-col">
                  <div className="text-subtitle-sb font-[600] text-gray-900">
                    {t('subscription:payment.complete.subTitle', '결제가 정상적으로 완료되었습니다.')}
                  </div>
                  <Spacing size={8} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
                  <div className="text-caption-1 font-[400] text-gray-600">
                    {t('subscription:payment.complete.onetimeDescription', {
                    defaultValues: '{{spaceName}} 스페이스가 이용 기간 동안 {{plan}} {{maxCcu}} 플랜으로 업그레이드됩니다.',
                    spaceName,
                    plan: plan.toUpperCase(),
                    maxCcu
                  })}
                  </div>
                  <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
                  <PlanApplyFeature title={Number(maxCcu) < 0 ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
                  ccu: maxCcu
                })} plan={plan as SubscribePlan} isJaPlan={isJaPlan} data-sentry-element="PlanApplyFeature" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
                </div>
              </section>
            </div>
          </div>
          <div className="flex w-full justify-end px-[40px] pb-[30px] pt-[20px]">
            <SuccessActionButtons spaceHashId={spaceHashId} entryMapHashId={entryMapHashId} data-sentry-element="SuccessActionButtons" data-sentry-source-file="PlanPaymentOnetimeSuccess.tsx" />
          </div>
        </div>
      </div>
      <style global jsx>{`
        body {
          background-color: #f7fafc;
        }
      `}</style>
    </ConfettiExplosionWrapper>;
};